<template>
    <TransitionRoot
        as="template"
        :show="open"
    >
        <Dialog
            as="div"
            class="fixed z-50 inset-0 overflow-y-auto"
            @close="closeModal"
        >
            <div
                class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0 dark:bg-neutral-600 dark:bg-opacity-60"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay class="fixed inset-0 bg-neutral-400 bg-opacity-60 transition-opacity backdrop-blur-sm dark:bg-neutral-900 dark:bg-opacity-40" />
                </TransitionChild>
        
                <span
                    class="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true"
                >&#8203;</span>
        
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        :class="[
                            'inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl',
                            'transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6',
                            'dark:bg-neutral-600'
                        ]"
                    >
                        <div
                            v-if="!fixed"
                            class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                        >
                            <button
                                type="button"
                                :class="[
                                    'bg-white rounded-md text-gray-400',
                                    'chat_transition hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:text-gray-500',
                                    'dark:bg-neutral-600 dark:hover:text-white dark:focus:ring-white dark:focus:text-white',
                                    {'opacity-50 cursor-no-drop': loading}
                                ]"
                                @click="closeModal"
                            >
                                <span class="sr-only">Fechar</span>
                
                                <XIcon
                                    class="h-6 w-6"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>
            
                        <div class="sm:flex sm:items-start">
                            <div
                                class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10"
                                :class="setType.class"
                            >
                                <Component
                                    :is="setType.icon"
                                    class="h-6 w-6 icon"
                                    aria-hidden="true"
                                />
                            </div>
              
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <DialogTitle
                                    as="h3"
                                    class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-300"
                                >
                                    {{ title }}
                                </DialogTitle>
                
                                <div class="mt-2">
                                    <p
                                        class="text-sm text-gray-500 dark:text-gray-400 "
                                        v-html="content"
                                    />
                                </div>
                            </div>
                        </div>
            
                        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <ButtonForm
                                v-if="execButton"
                                :content="execButton.text"
                                :icon="execButton.icon || null"
                                :color="execButton.color || 'red'"
                                :action="{exec: {action: execButton.emit, complement}}"
                                :loading="loading"
                                class="w-full justify-center sm:w-auto"
                                @button-action="emitButton"
                            />
              
                            <button
                                v-if="!fixed"
                                type="button"
                                :class="[
                                    'mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm text-sm',
                                    'px-4 py-2 bg-white font-medium text-gray-700 sm:mt-0 sm:w-auto sm: mx-2',
                                    'chat_transition hover:text-gray-500 outline-none focus:ring-2 focus:ring-gray-500',
                                    'dark:bg-neutral-600 dark:border-neutral-500 dark:text-gray-400 dark:hover:bg-neutral-500 dark:hover:text-white',
                                    'dark:focus:ring-white dark:focus:text-white',
                                    {'opacity-50 cursor-no-drop': loading}
                                ]"
                                @click="closeModal"
                            >
                                {{ cancelButton }}
                            </button>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
    import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
    import { InformationCircleIcon, ExclamationIcon, CheckCircleIcon, XIcon, ExclamationCircleIcon } from '@heroicons/vue/outline'
    import ButtonForm from '@/components/utils/ButtonForm';
  
    export default {
        name: 'Modal',
        components: {
            ButtonForm,
            Dialog,
            DialogOverlay,
            DialogTitle,
            TransitionChild,
            TransitionRoot,
            ExclamationIcon,
            CheckCircleIcon,
            ExclamationCircleIcon,
            XIcon,
        },
        props: {
            show: {
                type: Boolean,
                default: false,
            },
            type: {
                type: String,
                required: true,
            },
            title: {
                type: String,
                required: true,
            },
            content: {
                type: String,
                required: true,
            },
            cancelButton: {
                type: String,
                default: 'Cancelar',
            },
            execButton: {
                type: Object,
                default: () => {
                },
            },
            complement: {
                type: Object,
                default: null,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            fixed: {type: Boolean, default: false},
        },
        emits: ['execButton', 'closeModal'],
        data() {
            return {
                open: this.show,
            }
        },
        computed: {
            setType() {
                let icons = {
                    infor: {
                        icon: InformationCircleIcon,
                        class: 'type-info',
                    },
                    error: {
                        icon: ExclamationIcon,
                        class: 'type-error',
                    },
                    success: {
                        icon: CheckCircleIcon,
                        class: 'type-success',
                    },
                    warning: {
                        icon: ExclamationCircleIcon,
                        class: 'type-warning',
                    },
                }
        
                return icons[this.type] || icons['error']
            },
        },
        watch: {
            show(newValue) {
                this.open = newValue
            },
        },
        methods: {
            emitButton(data) {
                if (!this.execButton.emit || this.loading) return
        
                this.$emit('execButton', data.exec)
            },
      
            closeModal() {
                if (this.loading || this.fixed) return
  
                this.open = false
        
                this.$emit('closeModal')
            },
        },
    }
</script>

<style lang="scss" scoped>
.type-error {
  @apply bg-red-100;
  
  .icon {
    @apply text-red-600;
  }
}

.type-info {
    @apply bg-sky-100;
    
    .icon {
        @apply text-sky-600;
    }
}

.type-success {
    @apply bg-green-100;
    
    .icon {
        @apply text-green-600;
    }
}

.type-warning {
    @apply bg-yellow-100;
    
    .icon {
        @apply text-yellow-600;
    }
}
</style>